import React from 'react';
import {
    Box,
    Card,
    Chip,
    Typography,
    useTheme,
} from "@mui/material";
import {
    AccessTimeRounded,
    CancelRounded,
    ChatBubbleOutlineRounded,
    CheckCircleRounded,
    ChevronLeftRounded,
    HourglassEmptyRounded,
    MarkChatUnreadRounded,
    ReplyRounded,
} from "@mui/icons-material";
import { SupportStatus } from "../../helper/enums/SupportStatus";

export const STATUS_CONFIG = {
    AWAITING_USER: {
        label: "پاسخ کارشناس (منتظر شما)",
        color: "#0284c7",
        bgColor: "rgba(2, 132, 199, 0.1)",
        borderColor: "rgba(2, 132, 199, 0.25)",
        icon: <ReplyRounded sx={{ fontSize: 16 }} />,
    },
    AWAITING_EXPERT: {
        label: "در انتظار بررسی کارشناس",
        color: "#d97706",
        bgColor: "rgba(217, 119, 6, 0.1)",
        borderColor: "rgba(217, 119, 6, 0.25)",
        icon: <HourglassEmptyRounded sx={{ fontSize: 16 }} />,
    },
    PROCESSING: {
        label: "در حال پیگیری",
        color: "#6366f1",
        bgColor: "rgba(99, 102, 241, 0.1)",
        borderColor: "rgba(99, 102, 241, 0.25)",
        icon: <AccessTimeRounded sx={{ fontSize: 16 }} />,
    },
    COMPLETE: {
        label: "پایان‌یافته",
        color: "#059669",
        bgColor: "rgba(5, 150, 105, 0.1)",
        borderColor: "rgba(5, 150, 105, 0.25)",
        icon: <CheckCircleRounded sx={{ fontSize: 16 }} />,
    },
    CANCEL: {
        label: "لغو شده",
        color: "#dc2626",
        bgColor: "rgba(220, 38, 38, 0.1)",
        borderColor: "rgba(220, 38, 38, 0.25)",
        icon: <CancelRounded sx={{ fontSize: 16 }} />,
    },
};

const _SupportCardItem = ({ item, onClick }) => {
    const theme = useTheme();
    const config = STATUS_CONFIG[item.Status] || {
        label: SupportStatus[item.Status] || item.Status,
        color: '#64748b',
        bgColor: 'rgba(100, 116, 139, 0.1)',
        borderColor: 'rgba(100, 116, 139, 0.2)',
        icon: null,
    };
    const hasUnread = item.UnreadCount > 0;

    const formatPersianDate = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('fa-IR', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    return (
        <Card
            onClick={onClick}
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 3.5,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: hasUnread ? 'primary.main' : 'divider',
                bgcolor: hasUnread ? 'action.selected' : 'background.paper',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                    borderColor: 'primary.main',
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: hasUnread ? 'primary.main' : 'action.hover',
                            color: hasUnread ? '#fff' : 'text.secondary',
                            flexShrink: 0,
                        }}
                    >
                        {hasUnread ? (
                            <MarkChatUnreadRounded sx={{ fontSize: 20 }} />
                        ) : (
                            <ChatBubbleOutlineRounded sx={{ fontSize: 20 }} />
                        )}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: hasUnread ? 800 : 700,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: 'text.primary',
                            }}
                        >
                            {item.Title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            شناسه تیکت: #{item.Id}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    {hasUnread && (
                        <Chip
                            size="small"
                            color="error"
                            label={`${item.UnreadCount} پاسخ جدید`}
                            sx={{ fontWeight: 700, height: 24, fontSize: '0.75rem' }}
                        />
                    )}
                    <Chip
                        size="small"
                        icon={config.icon}
                        label={config.label}
                        sx={{
                            fontWeight: 700,
                            height: 26,
                            fontSize: '0.75rem',
                            color: config.color,
                            bgcolor: config.bgColor,
                            border: '1px solid',
                            borderColor: config.borderColor,
                            '& .MuiChip-icon': {
                                color: 'inherit',
                            },
                        }}
                    />
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pt: 1.2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <AccessTimeRounded sx={{ fontSize: 15, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                        {formatPersianDate(item.CreatedDate)}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        مشاهده گفتگو
                    </Typography>
                    <ChevronLeftRounded sx={{ fontSize: 18 }} />
                </Box>
            </Box>
        </Card>
    );
};

export default _SupportCardItem;
